import React from "react";
import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../../ui/dialog";
import {
  SparklesIcon,
  EyeIcon,
  Loader2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RefreshCwIcon,
  AlertCircleIcon,
} from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { GENERATION_DOMAINS } from "./types";

interface PreviewExample {
  domain: string;
  difficulty: string;
  messages: any[];
  model: string;
  usage?: { prompt_tokens?: number; completion_tokens?: number };
}

interface PreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  error: string | null;
  examples: PreviewExample[];
  index: number;
  setIndex: (index: number) => void;
  onRegenerate: () => void;
  teacherDisplayName: string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  onOpenChange,
  loading,
  error,
  examples,
  index,
  setIndex,
  onRegenerate,
  teacherDisplayName,
}) => {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EyeIcon className="w-5 h-5 text-maitai-green" />
            Preview Generated Examples
            {!loading && examples.length > 0 && (
              <Badge variant="outline" className="ml-2 text-xs font-gc-reg">{teacherDisplayName}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Loading (no examples yet) */}
        {loading && examples.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-maitai-green/20 animate-pulse" />
              <SparklesIcon className="w-6 h-6 text-maitai-green absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm font-gc-med text-foreground">Generating seed prompts...</p>
              <p className="text-xs text-muted-foreground">
                Creating diverse prompts, then running them through <span className="font-gc-med">{teacherDisplayName}</span>.
                Examples will appear as they complete.
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="text-center space-y-2">
              <p className="text-sm font-gc-med text-foreground">Preview generation failed</p>
              <p className="text-xs text-muted-foreground max-w-sm">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={onRegenerate} className="gap-2 mt-2">
              <RefreshCwIcon className="w-3.5 h-3.5" /> Try Again
            </Button>
          </div>
        )}

        {/* Examples */}
        {!error && examples.length > 0 && (
          <div className="flex flex-col gap-4 min-h-0 flex-1">
            {/* Navigation */}
            <div className="flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1.5">
                {examples.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-xs font-gc-med transition-all",
                      i === index ? "bg-maitai-green text-white shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {i + 1}
                  </button>
                ))}
                {loading && (
                  <div className="px-3 py-1.5 rounded-md bg-muted/50">
                    <Loader2Icon className="w-3 h-3 animate-spin text-maitai-green" />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setIndex(Math.max(0, index - 1))} disabled={index === 0} className="h-7 w-7 p-0">
                  <ChevronLeftIcon className="w-4 h-4" />
                </Button>
                <span className="text-xs text-muted-foreground font-gc-med min-w-[3rem] text-center">
                  {index + 1} / {examples.length}{loading && "+"}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setIndex(Math.min(examples.length - 1, index + 1))} disabled={index === examples.length - 1} className="h-7 w-7 p-0">
                  <ChevronRightIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Metadata */}
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800">
                {GENERATION_DOMAINS.find((d) => d.id === examples[index]?.domain)?.label || examples[index]?.domain}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">{examples[index]?.difficulty}</Badge>
              {examples[index]?.usage && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {(examples[index].usage!.prompt_tokens || 0).toLocaleString()} in /{" "}
                  {(examples[index].usage!.completion_tokens || 0).toLocaleString()} out tokens
                </span>
              )}
            </div>

            {/* Messages */}
            <div className="overflow-y-auto flex-1 min-h-0 rounded-lg border border-border">
              <div className="divide-y divide-border">
                {(examples[index]?.messages || []).map((msg: any, msgIdx: number) => (
                  <div key={msgIdx} className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] uppercase tracking-wider font-gc-med",
                          msg.role === "system" && "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
                          msg.role === "user" && "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
                          msg.role === "assistant" && "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                        )}
                      >
                        {msg.role}
                      </Badge>
                    </div>
                    <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed font-gc-reg">{msg.content}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 flex items-center justify-between sm:justify-between">
          <Button variant="outline" size="sm" onClick={onRegenerate} disabled={loading} className="gap-2">
            <RefreshCwIcon className="w-3.5 h-3.5" /> Regenerate
          </Button>
          <Button onClick={() => onOpenChange(false)} disabled={loading}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewModal;
